/**
 * 账单页纯视图逻辑 · 抽出来是为了能单测(金额显示算错比不显示更糟).
 *
 * 与后端 billing_core/payments.py 的 settlement_status() 同口径:
 * 结清状态由「已付 vs 应收」推导,四档 unpaid/partial/paid/overpaid,
 * **刻意不做「差几分算结清」的容差** —— 差额应走调整贷项显式记录。
 */
import type { UserRole } from "@/api/auth";
import type { SettlementStatus } from "@/api/billing";

/**
 * 能看「金额面」的角色 · 必须与后端 app/core/authz.py 的
 * PERMISSIONS["billing.read_money"] **逐字一致**(按字母序,便于跨层比对)。
 *
 * 三处共用这一个来源:前端路由门(App.tsx)、顶栏入口(CncLayout.tsx)、
 * 后端 require_roles。三份手挑必然漂移 —— 跨层守卫见
 * tests/core/test_authz.py::test_billing_money_roles_match_frontend。
 */
export const BILLING_MONEY_ROLES: readonly UserRole[] = [
  "auditor",
  "it_admin",
  "plant_manager",
  "platform_ops",
  "process_lead",
];

/** 分 → 人民币显示 · 始终两位小数,负数显示为「-¥x.xx」而不是「¥-x.xx」。 */
export function formatCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const yuan = Math.abs(cents) / 100;
  return `${sign}¥${yuan.toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export interface SettlementView {
  label: string;
  color: string;
  /** 是否属于「还欠钱」· 决定要不要在页面顶部计入待付总额 */
  outstanding: boolean;
}

const VIEWS: Record<SettlementStatus, SettlementView> = {
  unpaid: { label: "未支付", color: "red", outstanding: true },
  partial: { label: "部分支付", color: "orange", outstanding: true },
  paid: { label: "已结清", color: "green", outstanding: false },
  // 超收不是错误状态:多付的钱挂在账上,后续账期可抵扣
  overpaid: { label: "超额支付", color: "blue", outstanding: false },
  void: { label: "已作废", color: "default", outstanding: false },
};

export function settlementView(status: SettlementStatus): SettlementView {
  return (
    VIEWS[status] ?? { label: status, color: "default", outstanding: false }
  );
}

/**
 * 付款进度百分比(0-100)· 用于进度条。
 *
 * 应收为 0 时返回 100(而不是除零得 NaN):金额为 0 的账单本来就无需支付。
 * 超收时钳到 100,避免进度条溢出。
 */
export function paidPercent(amountCents: number, paidCents: number): number {
  if (amountCents <= 0) return 100;
  return Math.min(
    100,
    Math.max(0, Math.round((paidCents / amountCents) * 100)),
  );
}
